import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';

interface ConfirmDialogProps {
  title?: string;
  message: string;
  confirmPhrase?: string;
  onYes: () => void;
  onNo?: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title = 'Are you sure?',
  message,
  confirmPhrase,
  onYes,
  onNo,
  onCancel
}) => {
  const [typed, setTyped] = useState('');
  const canConfirm = !confirmPhrase || typed.trim() === confirmPhrase;

  return createPortal(
    <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 font-sans">
      <div className="relative bg-neutral-900 text-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-neutral-800 transition-all duration-200">
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-neutral-800 transition-all"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-900/20 flex items-center justify-center">
              <AlertTriangle className="text-amber-400 w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold">{title}</h3>
          </div>

          <p className={`text-sm text-gray-400 leading-relaxed ${confirmPhrase ? 'mb-4' : 'mb-8'}`}>{message}</p>

          {confirmPhrase ? (
            <div className="mb-8">
              <label className="block text-xs text-gray-500 mb-1">
                Type <span className="font-mono text-gray-300">{confirmPhrase}</span> to confirm
              </label>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                className="w-full bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2 text-sm text-white outline-none focus:ring-1 focus:ring-red-500"
                placeholder={confirmPhrase}
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <button
              onClick={onCancel}
              className="px-5 py-2.5 rounded-xl text-gray-400 text-sm font-bold hover:bg-neutral-800 transition-all"
            >
              No, Keep it
            </button>
            <button
              onClick={onYes}
              disabled={!canConfirm}
              className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-600 text-white text-sm font-bold shadow-lg shadow-red-600/20 active:scale-95 transition-all"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
